import { cs } from '../../lib/currency';
/**
 * Super Admin → Plans → Compare (iter318 wave 21)
 *
 * Side-by-side matrix of every feature in the catalog against every
 * active plan. Rows are features (grouped by category), columns are
 * plans sorted by ascending monthly price so the cheapest tier is
 * left-most. Cell content:
 *   ✓ green   — feature is directly included in the plan
 *   ⇣ blue    — feature is inherited via a dependency (auto-included)
 *   ✕ slate   — not included
 *
 * The data source is the same `/super-admin/plans/detailed` +
 * `/super-admin/features` endpoints as the main Plans page, so any
 * add / remove / edit action there automatically reflects here on
 * next mount (no cache) — plus we auto-poll every 15s and re-fetch
 * on window focus so a super-admin editing plans in another tab
 * sees changes in near-real-time.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2,
  ArrowLeft,
  Check,
  X as XIcon,
  ChevronDown,
  RefreshCw,
  Info,
} from "lucide-react";

const API =
  (typeof window !== "undefined" && window.location
    ? window.location.origin
    : process.env.REACT_APP_BACKEND_URL) + "/api";

const authHeader = () => {
  const token = localStorage.getItem("token") || localStorage.getItem("access_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const priceLabel = (n) =>
  n === null || n === undefined || n === "" ? "TBD" : `${cs()}${Number(n).toFixed(2)}`;

// Human-friendly category order — matches the order features are
// declared in the backend catalog so authors don't have to guess.
const CATEGORY_ORDER = [
  "core",
  "programs",
  "payments",
  "enrollments",
  "bookings",
  "camps",
  "tournaments",
  "events",
  "comms",
  "content",
];

const CATEGORY_LABELS = {
  core: "Core & Staff",
  programs: "Programs & Coaching",
  payments: "Payments",
  enrollments: "Enrollments & Billing",
  bookings: "Slot Bookings",
  camps: "Camps",
  tournaments: "Tournaments",
  events: "Events",
  comms: "Communications",
  content: "Content & Marketing",
};

function StateCell({ state }) {
  if (state === "direct") {
    return (
      <div
        className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-emerald-50 text-emerald-700"
        title="Included in this plan"
        data-testid="cell-state-direct"
      >
        <Check className="w-4 h-4" strokeWidth={3} />
      </div>
    );
  }
  if (state === "inherited") {
    return (
      <div
        className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-sky-50 text-sky-700"
        title="Auto-included via a dependency"
        data-testid="cell-state-inherited"
      >
        <ChevronDown className="w-4 h-4" strokeWidth={3} />
      </div>
    );
  }
  return (
    <div
      className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-slate-100 text-slate-400"
      title="Not included"
      data-testid="cell-state-missing"
    >
      <XIcon className="w-3.5 h-3.5" strokeWidth={3} />
    </div>
  );
}

export default function SuperAdminPlansCompare() {
  const [plans, setPlans] = useState([]);
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastLoadedAt, setLastLoadedAt] = useState(null);
  const [error, setError] = useState(null);

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const [p, f] = await Promise.all([
        axios.get(`${API}/super-admin/plans/detailed`, { headers: authHeader() }),
        axios.get(`${API}/super-admin/features`, { headers: authHeader() }),
      ]);
      setPlans(p.data.plans || []);
      setFeatures(f.data.features || []);
      setLastLoadedAt(new Date());
      setError(null);
    } catch (e) {
      const msg = e.response?.data?.detail || "Failed to load plan comparison.";
      setError(msg);
      if (!silent) toast.error(msg);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Initial load
  useEffect(() => {
    load();
  }, []);

  // Auto-refresh: 15s poll + on window focus. Silent so the operator
  // isn't spammed with toasts while comparing.
  useEffect(() => {
    const interval = setInterval(() => load(true), 15000);
    const onFocus = () => load(true);
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  // Sort plans by monthly price ascending; unknown prices sink to the
  // right of priced plans, then alphabetically by name as final tie-break.
  const sortedPlans = useMemo(() => {
    const active = plans.filter((p) => (p.status ?? "active") !== "archived");
    return [...active].sort((a, b) => {
      const ap = a.price_per_month;
      const bp = b.price_per_month;
      const aNum = ap === null || ap === undefined || ap === "" ? Infinity : Number(ap);
      const bNum = bp === null || bp === undefined || bp === "" ? Infinity : Number(bp);
      if (aNum !== bNum) return aNum - bNum;
      return (a.name || "").localeCompare(b.name || "");
    });
  }, [plans]);

  // Group features by category with a stable order — the same order
  // the operator sees on the Plans edit page.
  const groupedFeatures = useMemo(() => {
    const byCat = new Map();
    features.forEach((f) => {
      const cat = f.category || "other";
      if (!byCat.has(cat)) byCat.set(cat, []);
      byCat.get(cat).push(f);
    });
    // Sort within each category by declared name.
    byCat.forEach((arr) => arr.sort((a, b) => (a.name || "").localeCompare(b.name || "")));
    // Preserve CATEGORY_ORDER for known cats, then anything else alphabetical.
    const known = CATEGORY_ORDER.filter((c) => byCat.has(c));
    const extras = [...byCat.keys()].filter((c) => !CATEGORY_ORDER.includes(c)).sort();
    return [...known, ...extras].map((cat) => ({
      category: cat,
      label: CATEGORY_LABELS[cat] || cat,
      features: byCat.get(cat),
    }));
  }, [features]);

  // Pre-compute plan feature sets for O(1) cell lookup.
  const planSets = useMemo(() => {
    return sortedPlans.map((p) => ({
      id: p.id,
      direct: new Set(p.direct_feature_keys || p.included_feature_keys || []),
      effective: new Set(p.effective_feature_keys || p.included_feature_keys || []),
    }));
  }, [sortedPlans]);

  const cellState = (featureKey, planIdx) => {
    const set = planSets[planIdx];
    if (!set) return "missing";
    if (set.direct.has(featureKey)) return "direct";
    if (set.effective.has(featureKey)) return "inherited";
    return "missing";
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-500" data-testid="plans-compare-loading">
        <Loader2 className="w-5 h-5 animate-spin" /> Loading comparison…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="super-admin-plans-compare-page">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-3">
            <a
              href="/super-admin/plans"
              className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900"
              data-testid="plans-compare-back"
            >
              <ArrowLeft className="w-4 h-4" /> Plans
            </a>
          </div>
          <h1 className="text-3xl font-semibold tracking-tight mt-1">Compare plans</h1>
          <p className="text-sm text-slate-500 max-w-2xl">
            Side-by-side matrix of every feature in the catalog. Updates
            automatically when you edit plans on the Plans page.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {lastLoadedAt && (
            <span className="text-xs text-slate-400" data-testid="plans-compare-updated">
              Updated {lastLoadedAt.toLocaleTimeString()}
            </span>
          )}
          <button
            type="button"
            onClick={() => load(false)}
            disabled={refreshing}
            data-testid="plans-compare-refresh"
            className="inline-flex items-center gap-2 bg-white hover:bg-slate-50 text-slate-700 border border-slate-300 text-sm font-medium px-3 py-1.5 rounded disabled:opacity-60"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </header>

      {error && (
        <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-3" data-testid="plans-compare-error">
          {error}
        </div>
      )}

      <Legend />

      {sortedPlans.length === 0 ? (
        <div className="text-sm text-slate-500 bg-white border border-slate-200 rounded p-6 text-center">
          No active plans found. Add a plan on the Plans page first.
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="plans-compare-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="sticky left-0 z-10 bg-slate-50 text-left px-4 py-3 font-medium text-slate-600 border-r border-slate-200 min-w-[280px]">
                    Feature
                  </th>
                  {sortedPlans.map((p) => (
                    <th
                      key={p.id}
                      data-testid={`plans-compare-header-${p.id}`}
                      className="text-center px-3 py-3 font-medium text-slate-800 min-w-[140px]"
                    >
                      <div className="font-semibold">{p.name}</div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        {priceLabel(p.price_per_month)}
                        <span className="text-slate-400"> / mo</span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {groupedFeatures.map((grp) => (
                  <FragmentRows
                    key={grp.category}
                    group={grp}
                    plans={sortedPlans}
                    cellState={cellState}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function FragmentRows({ group, plans, cellState }) {
  return (
    <>
      <tr className="bg-slate-50/60 border-t border-b border-slate-200">
        <td
          colSpan={plans.length + 1}
          className="sticky left-0 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-slate-500"
        >
          {group.label}
        </td>
      </tr>
      {group.features.map((f) => (
        <tr key={f.key} className="border-t border-slate-100 hover:bg-slate-50/50">
          <td
            className="sticky left-0 z-10 bg-white group-hover:bg-slate-50/50 px-4 py-2.5 border-r border-slate-100"
            data-testid={`plans-compare-feature-${f.key}`}
          >
            <div className="font-medium text-slate-800 leading-tight">{f.name}</div>
            {f.description && (
              <div className="text-xs text-slate-500 mt-0.5 leading-snug">{f.description}</div>
            )}
            {f.depends_on && f.depends_on.length > 0 && (
              <div className="text-[10px] text-slate-400 mt-1 uppercase tracking-wider">
                needs {f.depends_on.join(" · ")}
              </div>
            )}
          </td>
          {plans.map((p, idx) => (
            <td
              key={p.id}
              data-testid={`plans-compare-cell-${f.key}-${p.id}`}
              className="text-center px-3 py-2.5"
            >
              <StateCell state={cellState(f.key, idx)} />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

function Legend() {
  return (
    <div className="flex flex-wrap items-center gap-4 text-xs text-slate-500 bg-white border border-slate-200 rounded p-3">
      <div className="inline-flex items-center gap-2">
        <div className="w-6 h-6 rounded-full bg-emerald-50 text-emerald-700 inline-flex items-center justify-center">
          <Check className="w-3.5 h-3.5" strokeWidth={3} />
        </div>
        Included in plan
      </div>
      <div className="inline-flex items-center gap-2">
        <div className="w-6 h-6 rounded-full bg-sky-50 text-sky-700 inline-flex items-center justify-center">
          <ChevronDown className="w-3.5 h-3.5" strokeWidth={3} />
        </div>
        Inherited via dependency
      </div>
      <div className="inline-flex items-center gap-2">
        <div className="w-6 h-6 rounded-full bg-slate-100 text-slate-400 inline-flex items-center justify-center">
          <XIcon className="w-3 h-3" strokeWidth={3} />
        </div>
        Not included
      </div>
      <div className="inline-flex items-center gap-2 text-slate-400">
        <Info className="w-3.5 h-3.5" />
        Auto-refreshes every 15s and on tab focus.
      </div>
    </div>
  );
}
